import { DocumentationRequestEmailEditForm } from "application/documentation-request/forms";
import { BaseEditViewStore } from "core/stores";
import { applicationContext } from "core/utils";
import { action, computed, observable } from "mobx";
import { DocumentationRequestService } from "application/documentation-request/services";
import { ModalParams } from 'core/models';
import { isEmpty } from "lodash";
import { FacilityOrderService, FacilityOrderStatusService, FacilityOrderTemplateService } from "../../facility-order/services";
import { LocalPatientService } from "../../patient/services";
import { LookupService } from "common/services";

@applicationContext
class DocumentationRequestEmailEditViewStore extends BaseEditViewStore {
    @observable accessor body = null;
    @observable accessor isLoading = null;
    @observable accessor fetchingData = null;
    @observable accessor isManuallyLoading = null;

    acceptedFiles = ".csv,.pdf";
    @observable accessor fileCount = 0;
    @observable.ref accessor selectedFiles = null;
    @observable accessor isUploadInProgress = false;
    @observable accessor totalFileSize = 0;
    @observable accessor isDocumentationRequestSent = false;
    @observable accessor emailSentInfo = '';
    @observable accessor facilityOrderStatus;
    @observable accessor isReviewed;
    @observable accessor facilityOrder;

    @computed
    get isDisabled() {
        return (
            !this.selectedFiles ||
            this.isUploadInProgress
        );
    }

    constructor(rootStore) {
        const documentationRequestService = new DocumentationRequestService(rootStore.application.baasic.apiClient);
        const facilityOrderId = rootStore.routerStore.routerState.params.facilityOrderId;
        const facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        super(rootStore, {
            name: "documentation-request-email-edit",
            Form: DocumentationRequestEmailEditForm,
            actions: () => {},
            FormClass: DocumentationRequestEmailEditForm,
        });

        this.documentationRequestService = documentationRequestService;
        this.facilityOrderId = facilityOrderId;
        this.getEmailPrefillData(facilityOrderId);
        this.loggedUserId = rootStore.userStore.applicationUser.id;
        this.uploadDocumentModal = new ModalParams({});
        this.sendManuallyModal = new ModalParams({});
        this.sendManuallyModal.onConfirm = async () => { await this.sendManualEmail(); };
        this.sendManuallyModal.onCancel = () => { this.sendManuallyConfirmationModal.close() };
        this.facilityOrderService = facilityOrderService;
        this.facilityOrderStatusService = new FacilityOrderStatusService(rootStore.application.baasic.apiClient);
        this.facilityOrderTemplateService = new FacilityOrderTemplateService(rootStore.application.baasic.apiClient);
        this.localPatientService = new LocalPatientService(rootStore.application.baasic.apiClient);
        this.lookupService = new LookupService(rootStore.application.baasic.apiClient);
    }

    @action.bound
    downloadSingleFile(fileForm) {
        const selected = this.selectedFiles.find(f => f.index === fileForm.index);
        if (!selected || !(selected.file instanceof Blob)) {
            return;
        }

        const blobUrl = window.URL.createObjectURL(selected.file);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileForm.fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
    }

    @action.bound
    async getEmailPrefillData(facilityOrderId) {
        try {
            this.fetchingData = true;
            const response = await this.documentationRequestService.getEmailPrefillData(facilityOrderId);

            this.facilityOrder = (await this.facilityOrderService.get(facilityOrderId, { embed: 'Facility,Facility.FacilityAreaOfCares,Facility.Corporate_,FacilityOrderStatuses' })).data;

            this.lookups = await this.lookupService.getLookups("ItemCategory");
            this.enteralItemCategoryId = this.lookups.itemCategory.find((s) => s.abrv === 'enteral').id;

            const masterPatient = (await this.localPatientService.getByPatientKey(this.facilityOrder.facility.masterPatientId, { fields: 'id' })).data;
            this.masterPatientId = masterPatient ? masterPatient.id : undefined;

            this.isDocumentationRequestSent = response.data.isDocumentationRequestSent;
            if(this.isDocumentationRequestSent)
            {
                const sentEmailResponse = (await this.facilityOrderService.getSentEmailInfo(facilityOrderId, "DocumentationRequest")).data;

                if (sentEmailResponse) {
                    const formattedDate = new Date(sentEmailResponse.eventDate).toLocaleString("en-US", {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true
                      });

                    this.emailSentInfo = `Sent by ${sentEmailResponse.name} on ${formattedDate}`;
                } else {
                    this.emailSentInfo = '';
                }
            }
            this.form.update(response.data);
            this.body = response.data.body;
            this.rootStore.titleStore.setTitle(response.data.facilityName ? `Documentation request - ${response.data.facilityName}` : 'Documentation request' );
            this.fetchingData = false;
        }
        catch (err) {
            let errorMessage = typeof err.data === "object" ? err.data.errorMessage : err.data;
            this.rootStore.notificationStore.error(errorMessage ? errorMessage : "DOCUMENTATION_REQUEST.ERROR_EMAIL_PREFILL");
            this.fetchingData = false;
        }
    }

    @action.bound
    async sendEmail() {
        try {
            this.isLoading = true;
            const resource = this.form.values();

            let formData = new FormData();
            if (this.selectedFiles){
                const totalSize = this.selectedFiles.reduce((sum, file) => sum + file.size, 0);
                if (totalSize > 20 * 1024 * 1024){ // 20MB
                    this.rootStore.notificationStore.error(`Total file size exceeds limit of 20MB`);
                    this.isLoading = false;
                    return;
                }

                for (const file of this.selectedFiles) {
                    formData.append("file", file.file, file.file.name);
                }
            }

            resource.files = null;
            resource.body = this.encodeUnicodeToBase64(resource.body);
            formData.append("data", JSON.stringify({ ...resource, facilityOrderId: this.facilityOrderId }));

            await this.getOrderStatus();
            await this.documentationRequestService.sendEmail(formData);

            if (!this.isDocumentationRequestSent)
            {
                await this.facilityOrderService.markInventoryReportAsSent(this.facilityOrderId, "DocumentationRequest");
            }
            else
            {
                this.isDocumentationRequestSent = true;
            }

            if (this.facilityOrderStatus && this.facilityOrderStatus.startedOn && !this.facilityOrderStatus.completedOn && this.isReviewed)
            {
                this.rootStore.notificationStore.success("Clinical Review successfully completed.");
            }
            this.isLoading = false;

            this.rootStore.notificationStore.success("Email successfully sent.");
        }
        catch (err) {
            this.isLoading = false;
            let errorMessage = typeof err.data === "object" ? err.data.errorMessage : err.data;
            this.rootStore.notificationStore.error(errorMessage ? errorMessage : "DOCUMENTATION_REQUEST.ERROR_EMAIL_SEND");
        }
    }

    async getOrderStatus() {
        var statusesResponse = await this.facilityOrderStatusService.find({ facilityOrderId: this.facilityOrderId, embed: "Status,StartedByUser,CompletedByUser" });
        let facilityOrderStatuses = statusesResponse.data.item;
        this.facilityOrderStatus = facilityOrderStatuses.find(i => i.status.abrv === 'clinical-review');

        let params = {
            pageSize: 1,
            facilityOrderId: this.facilityOrderId,
            isClinicalReviewed: false,
            fields: "id",
            itemCategoryIds: this.facilityOrder.isEnteralWeek ? [this.enteralItemCategoryId] : undefined,
            masterPatientId: this.facilityOrder.isEnteralWeek ? this.masterPatientId : undefined
        }

        const response = await this.facilityOrderTemplateService.find(params);
        this.isReviewed = response.data.item && !response.data.item.some(i => i);
    }

    @action.bound
    async signalRAction(message) {
        if(message.table == "SE") {
            if(message.column == "EmailType" && message.id == this.facilityOrderId)
            {
                if(message.text == "DocumentationRequest")
                {
                    this.isDocumentationRequestSent = true;

                    if(this.emailSentInfo == '')
                    {
                        const sentEmailResponse = (await this.facilityOrderService.getSentEmailInfo(this.facilityOrderId, "DocumentationRequest")).data;

                        if (sentEmailResponse) {
                            const formattedDate = new Date(sentEmailResponse.eventDate).toLocaleString("en-US", {
                                month: "2-digit",
                                day: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                                hour12: true
                            });

                            this.emailSentInfo = `Sent by ${sentEmailResponse.name} on ${formattedDate}`;
                        } else {
                            this.emailSentInfo = '';
                        }
                    }
                }
            }
        }
    }

    @action.bound
    async openSendManuallyPopup() {
        this.sendManuallyModal.open();
    }

    @action.bound
    async sendManualEmail() {
        this.isManuallyLoading = true;
        await this.getOrderStatus();
        if (this.facilityOrderStatus && this.facilityOrderStatus.startedOn && !this.facilityOrderStatus.completedOn && this.isReviewed)
        {
            this.rootStore.notificationStore.success("Clinical Review successfully completed.");
        }
        await this.facilityOrderService.markInventoryReportAsSent(this.facilityOrderId, "DocumentationRequest")
        this.isDocumentationRequestSent = true;

        this.sendManuallyModal.close();
        this.isManuallyLoading = false;
    }

    closePage() {
        window.close();
    }

    @action.bound
    async download() {
        const { notificationStore } = this.rootStore;

        const resource = this.form.values();
        var fileName = resource.subject + '.msg';

        var xhr = new XMLHttpRequest();
        xhr.open('POST', `${this.documentationRequestService.getDownloadUrl()}?contentDisposition=attachment&token=${
            this.rootStore.authStore.token.tokenUrl
        }&ttype=${
            this.rootStore.authStore.token.type}`, true);

        xhr.setRequestHeader('Asccept', '*/*');

        xhr.responseType = 'arraybuffer'; // this is required

        xhr.onload = function() {
            if (this.status == 200) {
                var blob = new Blob([this.response], { type: "application/octet-stream"});
                var link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = fileName;
                link.click();
                link.remove();
                // in case the Blob uses a lot of memory
                setTimeout(() => URL.revokeObjectURL(link.href), 7000);
            }
            else{
                notificationStore.error("Error occured while trying to download email.");
            }
        };

        let formData = new FormData();
        if (this.selectedFiles){
            const totalSize = this.selectedFiles.reduce((sum, file) => sum + file.file.size, 0);
                if (totalSize > 20 * 1024 * 1024){ // 20MB
                    this.rootStore.notificationStore.error(`Total file size exceeds limit of 20MB`);
                    return;
                }

            for (const file of this.selectedFiles) {
                formData.append("file", file.file, file.file.name);
            }
        }

        // create a JSON object
        const json = {
            userId: this.loggedUserId,
            body: this.encodeUnicodeToBase64(resource.body),
            cc: resource.cc,
            to: resource.to,
            replyTo: resource.replyTo,
            subject: resource.subject
        }
        formData.append("data", JSON.stringify(json));
        xhr.send(formData);

        if(!this.isDocumentationRequestSent){
            this.rootStore.notificationStore.warning('If sending the email manually, please click on the “Sent Manually” button to confirm that you’ve done so and complete the flow.');
        }
    }

    @action.bound
    async openUploadDocumentPopup() {
        this.uploadDocumentModal.open();
    }

    @action.bound
    onFileDrop({ affectedFiles }) {
        if (!this.selectedFiles) this.selectedFiles = [];

        let formFields = this.form.$('files').value || [];

        affectedFiles.forEach(file => {
            const rawFile = typeof file.getRawFile === 'function' ? file.getRawFile() : file;

            const idx = this.fileCount++;
            this.selectedFiles.push({ index: idx, file: rawFile });

            formFields.push({
                index: idx,
                fileName: rawFile.name,
                fileSize: this.truncateToTwoDecimals(rawFile.size / 1024 / 1024),
                name: rawFile.name.split('.').slice(0, -1).join('.')
            });
        });

        this.updateTotalFileSize();
        this.form.$('files').value = formFields;
    }

    @action.bound
    removeAttachment(index) {
        let formFields = this.form.$('files').value;
        formFields = formFields.filter(item => item.index !== index);
        this.selectedFiles.splice(index, 1); // remove one element from index
        this.updateTotalFileSize();

        if (isEmpty(this.selectedFiles)) this.selectedFiles = null;

        this.fileCount = 0;
        formFields.forEach(item => { item.index = this.fileCount++ });

        this.form.$('files').value = formFields;
    }

    @action
    encodeUnicodeToBase64(str) {
        const bytes = new TextEncoder().encode(str);
        let binary = '';
        bytes.forEach(byte => binary += String.fromCharCode(byte));
        return btoa(binary);
    }

    updateTotalFileSize(){
        this.totalFileSize = this.truncateToTwoDecimals(this.selectedFiles.reduce((sum, file) => sum + file.file.size, 0) / 1024 / 1024);
    }

    truncateToTwoDecimals(number) {
        return Math.floor(number * 100) / 100;
      }

}

export default DocumentationRequestEmailEditViewStore;
